// Transitions PR-1 — React Query hooks for the loan Transitions tab + the
// cross-loan console. Every mutation invalidates the transitions scope AND
// the loan summary/health (a transition is loan-state-adjacent, the usePayoff
// precedent).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  loanTransitionsApi,
  type ChangeLoanTransitionStatusRequest,
  type CreateLoanTransitionRequest,
  type ExecuteAssumptionRequest,
  type ExecuteBmspExtensionRequest,
  type RecordPrepaymentNoticeRequest,
  type TransitionExplainRequest,
} from "@/api/portfolioAdmin/loanTransitionsApi";
import { loanTransitionsKeys } from "./loanTransitionsKeys";
import { invalidateLoanSummaryAndHealth } from "./invalidation";

export function useLoanTransitions(loanId: string | undefined) {
  return useQuery({
    queryKey: loanTransitionsKeys.list(loanId ?? ""),
    queryFn: () => loanTransitionsApi.list(loanId!),
    enabled: Boolean(loanId),
  });
}

export function useLoanTransitionDetail(
  loanId: string | undefined,
  transitionId: string | undefined,
) {
  return useQuery({
    queryKey: loanTransitionsKeys.detail(loanId ?? "", transitionId ?? ""),
    queryFn: () => loanTransitionsApi.get(loanId!, transitionId!),
    enabled: Boolean(loanId) && Boolean(transitionId),
  });
}

export function usePayoffQuotePreview(loanId: string | undefined, asOf?: string, enabled = true) {
  return useQuery({
    queryKey: loanTransitionsKeys.quotePreview(loanId ?? "", asOf),
    queryFn: () => loanTransitionsApi.quotePreview(loanId!, asOf),
    enabled: Boolean(loanId) && enabled,
  });
}

export function usePayoffQuoteHistory(loanId: string | undefined) {
  return useQuery({
    queryKey: loanTransitionsKeys.quoteHistory(loanId ?? ""),
    queryFn: () => loanTransitionsApi.quoteHistory(loanId!),
    enabled: Boolean(loanId),
  });
}

export function useTransitionsConsole() {
  return useQuery({
    queryKey: loanTransitionsKeys.console(),
    queryFn: () => loanTransitionsApi.consoleOpen(),
  });
}

/** Transitions PR-2 — the loan's BMSP covenant countdown (null = not a live
 *  balloon loan). */
export function useBmspCountdown(loanId: string | undefined) {
  return useQuery({
    queryKey: loanTransitionsKeys.bmspCountdown(loanId ?? ""),
    queryFn: () => loanTransitionsApi.bmspCountdown(loanId!),
    enabled: Boolean(loanId),
  });
}

/** Transitions PR-2 — the cross-loan BMSP countdown queue (console). */
export function useBmspQueue() {
  return useQuery({
    queryKey: loanTransitionsKeys.bmspQueue(),
    queryFn: () => loanTransitionsApi.bmspQueue(),
  });
}

/** Transitions PR-3 — the AwaitingLinkedDeal picker source (in-progress
 *  deals). Fetched only while the picker is visible. */
export function useLinkableDeals(enabled: boolean) {
  return useQuery({
    queryKey: loanTransitionsKeys.linkableDeals(),
    queryFn: () => loanTransitionsApi.linkableDeals(),
    enabled,
  });
}

/** Shared post-mutation freshness: the loan's transitions scope, the console,
 *  and the loan summary + health. */
function useInvalidateTransitionScope(loanId: string) {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: loanTransitionsKeys.list(loanId) }),
      qc.invalidateQueries({ queryKey: loanTransitionsKeys.console() }),
      invalidateLoanSummaryAndHealth(qc, loanId),
    ]);
  };
}

export function useCreateLoanTransition(loanId: string) {
  const invalidate = useInvalidateTransitionScope(loanId);
  return useMutation({
    mutationFn: (body: CreateLoanTransitionRequest) =>
      loanTransitionsApi.create(loanId, body),
    onSuccess: () => void invalidate(),
  });
}

export function useChangeLoanTransitionStatus(loanId: string) {
  const invalidate = useInvalidateTransitionScope(loanId);
  return useMutation({
    mutationFn: (vars: { transitionId: string; body: ChangeLoanTransitionStatusRequest }) =>
      loanTransitionsApi.changeStatus(loanId, vars.transitionId, vars.body),
    onSuccess: () => void invalidate(),
  });
}

export function useRecordPrepaymentNotice(loanId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateTransitionScope(loanId);
  return useMutation({
    mutationFn: (vars: { transitionId: string; body: RecordPrepaymentNoticeRequest }) =>
      loanTransitionsApi.recordPrepaymentNotice(loanId, vars.transitionId, vars.body),
    onSuccess: () => {
      void invalidate();
      // The notice persisted the binding statement — refresh quote history too.
      void qc.invalidateQueries({ queryKey: loanTransitionsKeys.quoteHistory(loanId) });
    },
  });
}

/** PR-4a — execute a Scheduled assumption/hardship transition. The obligor
 *  swap changes the borrower everywhere (summary, health, roster, portal
 *  links), so the whole transition scope + loan summary/health refresh. */
export function useExecuteAssumption(loanId: string) {
  const invalidate = useInvalidateTransitionScope(loanId);
  return useMutation({
    mutationFn: (vars: { transitionId: string; body: ExecuteAssumptionRequest }) =>
      loanTransitionsApi.executeAssumption(loanId, vars.transitionId, vars.body),
    onSuccess: () => void invalidate(),
  });
}

/** PR-4b — execute a Scheduled BMSP maturity extension. The amendment moves
 *  the loan's maturity/term (summary-visible) and collects the 1% fee, so the
 *  whole transition scope + loan summary/health refresh. */
export function useExecuteBmspExtension(loanId: string) {
  const invalidate = useInvalidateTransitionScope(loanId);
  return useMutation({
    mutationFn: (vars: { transitionId: string; body: ExecuteBmspExtensionRequest }) =>
      loanTransitionsApi.executeBmspExtension(loanId, vars.transitionId, vars.body),
    onSuccess: () => void invalidate(),
  });
}

/** PR-5 — the path-wizard "Ask AI" coach. */
export function useExplainTransitionPath() {
  return useMutation({
    mutationFn: (vars: { loanId: string; body: TransitionExplainRequest }) =>
      loanTransitionsApi.explainPath(vars.loanId, vars.body),
    // no-invalidation: read-only AI explanation — nothing server-side changes.
  });
}

export function useIssuePayoffQuote(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { asOf?: string }) => loanTransitionsApi.quoteIssue(loanId, vars.asOf),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: loanTransitionsKeys.quoteHistory(loanId) });
    },
  });
}
